'use client';

import { c as _c } from "react/compiler-runtime";
import type { EditorView } from '@codemirror/view';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { ClientUploadedFileData } from 'uploadthing/types';
import { useMediaQuery } from 'usehooks-ts';
import { MarkdownEditorRestoreContext } from '../../_context/MarkdownEditorRestoreContext';
import { type DbAttachment, mapAttachmentsToFiles } from '../Attachments';
import { EditorLoader, SingleViewEditor, SplitViewEditor } from './_components';
import { useEditorLocalStorage } from './_hooks/useEditorLocalStorage';
import { useEditorRestoreState } from './_hooks/useEditorRestoreState';
import { ClientPreview } from './ClientPreview';
import { Editor } from './Editor';
import { useContentFormatter } from './Editor/_hooks/useContentFormatter';
import { SCROLL_AREA, type TreeData, useSyncScroll } from './Editor/_hooks/useSyncScroll';
export type MarkdownContent = {
  markdown: string;
  attachments?: DbAttachment[];
};
export type MarkdownEditorProps = {
  value?: MarkdownContent;
  singleView?: boolean;
  height?: number;
  disabled?: boolean;
  markdownOnly?: boolean;
  small?: boolean;
  onChange?: (value: {
    markdown: string;
    attachments: DbAttachment[];
  }) => void;
  unsavedChangesDialog?: React.ReactNode;
};
export const MarkdownEditor: React.FC<MarkdownEditorProps> = t0 => {
  const $ = _c(91);
  const {
    value,
    singleView,
    height,
    disabled: t1,
    markdownOnly: t2,
    small: t3,
    onChange,
    unsavedChangesDialog
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const markdownOnly = t2 === undefined ? false : t2;
  const small = t3 === undefined ? false : t3;
  const restoreState = useEditorRestoreState(value, onChange);
  const [initialValue] = useEditorLocalStorage(value);
  const {
    format,
    isFormatting
  } = useContentFormatter();
  const content = value?.markdown ?? initialValue.markdown;
  value?.attachments;
  const t4 = value?.attachments ?? initialValue.attachments;
  let t5;
  if ($[0] !== t4) {
    t5 = mapAttachmentsToFiles(t4);
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const files = t5;
  const [originalContent, setOriginalContent] = useState(content);
  const [showDiff, setShowDiff] = useState(false);
  const [currentScrollArea, setCurrentScrollArea] = useState(null);
  const [editorReady, setEditorReady] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const previewRef = useRef(null);
  const editorViewRef = useRef(null);
  const treeDataRef = useRef(null);
  const editorContainerRef = useRef(null);
  const diffContentRef = useRef(null);
  let t6;
  if ($[2] !== currentScrollArea) {
    t6 = {
      previewRef,
      editorViewRef,
      treeDataRef,
      currentScrollArea,
      editorContainerRef
    };
    $[2] = currentScrollArea;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const {
    handleEditorScroll,
    handlePreviewScroll,
    computePosition,
    getCurrentEditorScrollPosition,
    getCurrentPreviewScrollPosition,
    syncToPreview,
    syncToEditor
  } = useSyncScroll(t6);
  let t7;
  if ($[4] !== files || $[5] !== isFormatting || $[6] !== onChange || $[7] !== showDiff) {
    t7 = value_0 => {
      if (showDiff) {
        diffContentRef.current = value_0;
        return;
      }
      if (!isFormatting) {
        onChange?.({
          markdown: value_0,
          attachments: files.filter(_temp).map(_temp2)
        });
      }
    };
    $[4] = files;
    $[5] = isFormatting;
    $[6] = onChange;
    $[7] = showDiff;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleContentChange = t7;
  let t8;
  if ($[9] !== content || $[10] !== onChange) {
    t8 = newFiles => {
      onChange?.({
        markdown: content,
        attachments: newFiles.map(_temp3)
      });
    };
    $[9] = content;
    $[10] = onChange;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleFilesChange = t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = view => {
      editorViewRef.current = view;
      setEditorReady(true);
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const handleCreateEditor = t9;
  let t10;
  if ($[13] !== content || $[14] !== format || $[15] !== onChange || $[16] !== value?.attachments) {
    t10 = async () => {
      const formatted = await format(content);
      if (!formatted) {
        return;
      }
      setOriginalContent(content);
      onChange?.({
        markdown: formatted,
        attachments: value?.attachments ?? []
      });
      setShowDiff(true);
    };
    $[13] = content;
    $[14] = format;
    $[15] = onChange;
    $[16] = value?.attachments;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  value?.attachments;
  const handleFormatContent = t10;
  let t11;
  if ($[18] !== content || $[19] !== onChange || $[20] !== value?.attachments) {
    t11 = () => {
      const finalContent = diffContentRef.current ?? content;
      diffContentRef.current = null;
      setShowDiff(false);
      setOriginalContent(finalContent);
      onChange?.({
        markdown: finalContent,
        attachments: value?.attachments ?? []
      });
    };
    $[18] = content;
    $[19] = onChange;
    $[20] = value?.attachments;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  value?.attachments;
  const handleDiffComplete = t11;
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const isSingleView = singleView ?? isMobile;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setCurrentScrollArea(SCROLL_AREA.EDITOR);
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = !isFormatting && !disabled;
  let t14;
  if ($[23] !== files || $[24] !== handleFilesChange) {
    t14 = attachment => handleFilesChange([...files, attachment]);
    $[23] = files;
    $[24] = handleFilesChange;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== handleFormatContent || $[27] !== singleView) {
    t15 = {
      singleView,
      onFormat: handleFormatContent
    };
    $[26] = handleFormatContent;
    $[27] = singleView;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== content || $[30] !== handleContentChange || $[31] !== handleDiffComplete || $[32] !== handleEditorScroll || $[33] !== originalContent || $[34] !== showDiff || $[35] !== small || $[36] !== t13 || $[37] !== t14 || $[38] !== t15) {
    t16 = <Editor ref={editorContainerRef} content={content} setContent={handleContentChange} originalContent={originalContent} onCreateEditor={handleCreateEditor} onScroll={handleEditorScroll} onMouseEnter={t12} showDiff={showDiff} onDiffComplete={handleDiffComplete} isEditable={t13} small={small} onAddAttachment={t14} passDownProps={t15} />;
    $[29] = content;
    $[30] = handleContentChange;
    $[31] = handleDiffComplete;
    $[32] = handleEditorScroll;
    $[33] = originalContent;
    $[34] = showDiff;
    $[35] = small;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const EditorSection = t16;
  let t17;
  if ($[40] !== content) {
    t17 = {
      markdown: content
    };
    $[40] = content;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => setCurrentScrollArea(SCROLL_AREA.PREVIEW);
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setPreviewReady(true);
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== handlePreviewScroll || $[45] !== t17) {
    t20 = <ClientPreview ref={previewRef} content={t17} onScroll={handlePreviewScroll} onMouseEnter={t18} treeDataRef={treeDataRef} onReady={t19} className="px-6" />;
    $[44] = handlePreviewScroll;
    $[45] = t17;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const PreviewSection = t20;
  if (isSingleView) {
    let t21;
    if ($[47] !== editorReady || $[48] !== height) {
      t21 = !editorReady && <EditorLoader height={height} />;
      $[47] = editorReady;
      $[48] = height;
      $[49] = t21;
    } else {
      t21 = $[49];
    }
    let t22;
    if ($[50] !== computePosition || $[51] !== getCurrentEditorScrollPosition || $[52] !== getCurrentPreviewScrollPosition || $[53] !== syncToEditor || $[54] !== syncToPreview) {
      t22 = {
        computePosition,
        getCurrentEditorScrollPosition,
        getCurrentPreviewScrollPosition,
        syncToPreview,
        syncToEditor
      };
      $[50] = computePosition;
      $[51] = getCurrentEditorScrollPosition;
      $[52] = getCurrentPreviewScrollPosition;
      $[53] = syncToEditor;
      $[54] = syncToPreview;
      $[55] = t22;
    } else {
      t22 = $[55];
    }
    let t23;
    if ($[56] !== EditorSection || $[57] !== PreviewSection || $[58] !== disabled || $[59] !== editorReady || $[60] !== files || $[61] !== handleFilesChange || $[62] !== height || $[63] !== markdownOnly || $[64] !== small || $[65] !== t22) {
      t23 = <SingleViewEditor editorReady={editorReady} EditorSection={EditorSection} PreviewSection={PreviewSection} height={height} markdownOnly={markdownOnly} files={files} setFiles={handleFilesChange} disabled={disabled} small={small} syncScroll={t22} />;
      $[56] = EditorSection;
      $[57] = PreviewSection;
      $[58] = disabled;
      $[59] = editorReady;
      $[60] = files;
      $[61] = handleFilesChange;
      $[62] = height;
      $[63] = markdownOnly;
      $[64] = small;
      $[65] = t22;
      $[66] = t23;
    } else {
      t23 = $[66];
    }
    let t24;
    if ($[67] !== restoreState || $[68] !== t21 || $[69] !== t23 || $[70] !== unsavedChangesDialog) {
      t24 = <MarkdownEditorRestoreContext.Provider value={restoreState}>{t21}{t23}{unsavedChangesDialog}</MarkdownEditorRestoreContext.Provider>;
      $[67] = restoreState;
      $[68] = t21;
      $[69] = t23;
      $[70] = unsavedChangesDialog;
      $[71] = t24;
    } else {
      t24 = $[71];
    }
    return t24;
  }
  let t21;
  if ($[72] !== editorReady || $[73] !== height || $[74] !== previewReady) {
    t21 = !editorReady && !previewReady && <EditorLoader height={height} />;
    $[72] = editorReady;
    $[73] = height;
    $[74] = previewReady;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  let t22;
  if ($[76] !== EditorSection || $[77] !== PreviewSection || $[78] !== disabled || $[79] !== editorReady || $[80] !== files || $[81] !== handleFilesChange || $[82] !== height || $[83] !== markdownOnly || $[84] !== previewReady) {
    t22 = <SplitViewEditor editorReady={editorReady} previewReady={previewReady} EditorSection={EditorSection} PreviewSection={PreviewSection} height={height} markdownOnly={markdownOnly} files={files} setFiles={handleFilesChange} disabled={disabled} />;
    $[76] = EditorSection;
    $[77] = PreviewSection;
    $[78] = disabled;
    $[79] = editorReady;
    $[80] = files;
    $[81] = handleFilesChange;
    $[82] = height;
    $[83] = markdownOnly;
    $[84] = previewReady;
    $[85] = t22;
  } else {
    t22 = $[85];
  }
  let t23;
  if ($[86] !== restoreState || $[87] !== t21 || $[88] !== t22 || $[89] !== unsavedChangesDialog) {
    t23 = <MarkdownEditorRestoreContext.Provider value={restoreState}>{t21}{t22}{unsavedChangesDialog}</MarkdownEditorRestoreContext.Provider>;
    $[86] = restoreState;
    $[87] = t21;
    $[88] = t22;
    $[89] = unsavedChangesDialog;
    $[90] = t23;
  } else {
    t23 = $[90];
  }
  return t23;
};
function _temp(file) {
  return file !== undefined;
}
function _temp2(file_0) {
  return {
    name: file_0.name,
    url: file_0.ufsUrl
  };
}
function _temp3(file_1) {
  return {
    name: file_1.name,
    url: file_1.ufsUrl
  };
}